export type DayHours = {
  open: number[] | null; close: number[] | null;
  breakStart: number[] | null; breakEnd: number[] | null; off: boolean;
};
export type Holiday = { year: number | null; month: number; day: number; recurring: boolean };

export function toMinutes(t: number[] | null | undefined): number | null {
  if (!t || t.length < 2) return null;
  return t[0] * 60 + t[1];
}

/** number[] [h,m] → 12시간 표기 "3:00 PM". 미설정(null)이면 "". */
export function formatTime12(t: number[] | null | undefined): string {
  const m = toMinutes(t);
  if (m == null) return "";
  let h = Math.floor(m / 60);
  const min = m % 60;
  const ampm = h < 12 ? "AM" : "PM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min.toString().padStart(2, "0")} ${ampm}`;
}

export function isInBreak(day: DayHours, nowMin: number): boolean {
  const bs = toMinutes(day.breakStart), be = toMinutes(day.breakEnd);
  return bs != null && be != null && bs < be && nowMin >= bs && nowMin < be;
}

/** open~close 윈도우 안인가(off·break 무시). 오픈/종료 미설정(또는 동일)이면 '항상 윈도우 내'. */
export function isInOpenWindow(day: DayHours, nowMin: number): boolean {
  const open = toMinutes(day.open), close = toMinutes(day.close);
  if (open == null || close == null || open === close) return true; // 미설정 = 영업중
  return close < open
    ? (nowMin >= open || nowMin <= close)     // 자정 넘김
    : (nowMin >= open && nowMin <= close);    // 일반
}

/** 닫힌 이유를 요청 우선순위(off → 영업시간외 → 휴게)대로 반환. 영업중이면 null.
 *  off인 날엔 그 요일에 break 시각이 남아있어도 'off'가 우선한다(휴게로 오표시 방지). */
export function closedReason(day: DayHours, nowMin: number): "off" | "outside" | "break" | null {
  if (day.off) return "off";                        // 1) 요일별 휴무
  if (!isInOpenWindow(day, nowMin)) return "outside"; // 2) 영업시간 밖(다음 오픈 안내 대상)
  if (isInBreak(day, nowMin)) return "break";        // 3) 영업 윈도우 내 휴게
  return null;
}

export function isOpenNow(day: DayHours, nowMin: number): boolean {
  return closedReason(day, nowMin) === null;
}

export function isHolidayToday(holidays: Holiday[] | null | undefined, y: number, m: number, d: number): boolean {
  if (!holidays) return false;
  return holidays.some(h =>
    h.month === m && h.day === d && (h.recurring || h.year === y));
}

export function pickDayHours(shop: any, dow: number): DayHours {
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const d = days[dow];
  return {
    open: shop[`${d}OpeningTime`] as unknown as number[] | null,
    close: shop[`${d}ClosingTime`] as unknown as number[] | null,
    breakStart: shop[`${d}BreakStart`] ?? null,
    breakEnd: shop[`${d}BreakEnd`] ?? null,
    off: shop[`${d}Off`] ?? false,
  };
}

export type NextOpen = { date: Date; open: number[]; isToday: boolean };

/** from(오늘) 포함 최대 7일 앞으로 스캔해 "다음 영업 시작"을 찾는다.
 *  off/holiday인 날은 건너뛰고, 오늘은 아직 오픈 전(now < open)일 때만 후보로 삼는다.
 *  오픈 시각 미설정(항상영업)인 날은 건너뛴다. 7일 내 못 찾으면 null. */
/** 전날 자정 넘김(wrap) 세션의 이른 아침 tail 구간인지.
 *  조건: 전날이 close<open(자정 넘김)이고, 지금이 전날 close 이하이며, 전날이 휴무/휴일이 아닐 것.
 *  전날 세션 소속이므로 오늘의 off/휴일/영업시간과는 무관하게 판정한다.
 *  (break가 자정을 넘기는 경우는 미지원 — isInBreak가 bs<be만 처리.) */
function inPrevDayWrapTail(
  shop: any, now: Date, holidays: Holiday[] | null | undefined, nowMin: number
): boolean {
  const prev = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isHolidayToday(holidays, prev.getFullYear(), prev.getMonth() + 1, prev.getDate())) return false;
  const pday = pickDayHours(shop, prev.getDay());
  if (pday.off) return false;
  const po = toMinutes(pday.open), pc = toMinutes(pday.close);
  if (po == null || pc == null) return false;
  return pc < po && nowMin <= pc;   // 전날이 wrap이고 지금이 그 tail 안
}

/** 매장이 지금 영업 중인가 — splash의 open 판정 단일 소스(오케스트레이터).
 *  1) 오늘 휴일이 아니고 오늘 영업시간 기준 영업중이면 open.
 *  2) 그 외엔 전날 wrap 세션 tail을 폴백 검사(전날 세션은 오늘 off/휴일과 무관하게 이어짐).
 *  순수 per-day 헬퍼(isInOpenWindow/isInBreak/closedReason)는 건드리지 않는다. */
export function isShopOpenNow(
  shop: any, now: Date, holidays: Holiday[] | null | undefined
): boolean {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayHoliday = isHolidayToday(holidays, now.getFullYear(), now.getMonth() + 1, now.getDate());
  if (!todayHoliday && isOpenNow(pickDayHours(shop, now.getDay()), nowMin)) return true;
  return inPrevDayWrapTail(shop, now, holidays, nowMin);
}

export function nextOpening(
  shop: any,
  from: Date,
  holidays: Holiday[] | null | undefined
): NextOpen | null {
  const nowMin = from.getHours() * 60 + from.getMinutes();
  for (let i = 0; i <= 7; i++) {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
    if (isHolidayToday(holidays, d.getFullYear(), d.getMonth() + 1, d.getDate())) continue;
    const day = pickDayHours(shop, d.getDay());
    if (day.off) continue;
    const openMin = toMinutes(day.open);
    if (day.open == null || openMin == null) continue;   // 오픈 미설정 = 스킵
    if (i === 0 && nowMin >= openMin) continue;           // 오늘은 오픈 전만
    return { date: d, open: day.open as number[], isToday: i === 0 };
  }
  return null;
}
